import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import DigitalDispatchWebPubSubService from 'src/services/DigitalDispatchWebPubSubService';
import DigitalDispatchService from 'src/services/DigitalDispatchService';
import {
  DIGITAL_DISPATCH_LIVE_UPDATE_TYPE_DISPATCH_STATUS,
  DIGITAL_DISPATCH_LIVE_UPDATE_TYPE_LOCATION_STATUS,
  DIGITAL_DISPATCH_LIVE_UPDATE_TYPE_POSTAPPROVAL_STATUS
} from 'src/constants/digitalDispatchWebPubSub';
import { DIGITAL_DISPATCH_NETWORK_TOWBOOK } from 'src/constants/digitalProviderNetwork';
import {
  towBookDispatchStatusUpdateParser,
  towBookDispatchLocationUpdateParser,
  towBookDispatchPostApprovalUpdateParser
} from 'src/utils/towBookDispatchUpdateParser';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';

const digitalDispatchWebPubSubService = new DigitalDispatchWebPubSubService();
const digitalDispatchService = new DigitalDispatchService();

/**
 * Sets up a connection to an Azure WebPubSub connection
 * group that only listens to updates from the dispatch with
 * the specified dispatchId . Also fetches and
 * returns the intitial set of records, adding the new incoming
 * update to the returned array
 * @param {number} dispatchId
 * @param {boolean} omitPostApprovalEventsFetch
 */
const useDigitalDispatchLiveDispatchUpdates = (dispatchId, eventName, omitPostApprovalEventsFetch = false) => {
  const appInsights = useAppInsightsContext();
  const [isConnected, setIsConnected] = useState(false);
  const [isFetchingClientAccessUri, setIsFetchingClientAccessUri] = useState(true);
  const [dispatchStatusUpdates, setDispatchStatusUpdates] = useState(null);
  const [
    areDispatchStatusUpdatesLoading,
    setAreDispatchStatusUpdatesLoading
  ] = useState(false);
  const [dispatchLocationUpdates, setDispatchLocationUpdates] = useState(null);
  const [dispatchPostApprovalUpdates, setDispatchPostApprovalUpdates] = useState(null);
  const [
    areDispatchLocationUpdatesLoading,
    setAreDispatchLocationUpdatesLoading
  ] = useState(false);
  const [
    areDispatchPostApprovalUpdatesLoading,
    setAreDispatchPostApprovalUpdatesLoading
  ] = useState(false);
  const [webPubSubError, setWebPubSubError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const azureWebPubSubError = useTrackEvent(
    appInsights,
    eventName,
    {
      error: webPubSubError,
      errorMessage: webPubSubError?.message
    }
  );

  useEffect(() => {
    if (webPubSubError) {
      azureWebPubSubError();
    }
  }, [webPubSubError]);

  /**
   * @param {number} dispatchId - The dispatch ID (from Qore);
   * set ups the WebPubSub connection and creates a group for
   * listening for all updates for this particular dispatch
   * (dispatch statuses, location updates, post approval events)
   * @returns {Promise<void>}
   */
  const setUpWebPubSubClientInstance = async () => {
    try {
      setIsFetchingClientAccessUri(true);
      await digitalDispatchWebPubSubService.setWebPubSubClientToListenToOneDispatch(dispatchId);
      digitalDispatchWebPubSubService.onConnected(() => {
        setIsConnected(true);
      });
      digitalDispatchWebPubSubService.onDisconnected(() => {
        setIsConnected(false);
      });
      digitalDispatchWebPubSubService.onStopped(() => {
        setIsConnected(false);
      });
    } catch (error) {
      enqueueSnackbar(error?.message ?? 'Failed to fetch client access uri for live updates', {
        variant: 'error',
        preventDuplicate: true
      });
    } finally {
      setIsFetchingClientAccessUri(false);
    }
  };

  /**
   * Callback used when WebPubSub event handler receives a new group message
   * from the group set up to listen for any update on
   * this particular dispatch specified by the dispatchId
   * @param {Object} dispatchUpdate
   * @returns {void}
   */
  function addDispatchStatusUpdate(dispatchUpdate) {
    try {
      if (dispatchUpdate) {
        setAreDispatchStatusUpdatesLoading(true);
        const { webPubSubUpdateType } = dispatchUpdate;
        if (DIGITAL_DISPATCH_LIVE_UPDATE_TYPE_DISPATCH_STATUS === webPubSubUpdateType) {
          const isDispatchUpdatePresent = dispatchStatusUpdates ? Boolean(dispatchStatusUpdates
            .find(({ id }) => id === dispatchUpdate.id)) : false;
          if (!isDispatchUpdatePresent) {
            if (dispatchUpdate.digitalProviderNetwork === DIGITAL_DISPATCH_NETWORK_TOWBOOK) {
              const towBookDispatchUpdate = towBookDispatchStatusUpdateParser(dispatchUpdate);

              setDispatchStatusUpdates((previousUpdates) => [
                towBookDispatchUpdate, ...previousUpdates
              ]);
            } else {
              setDispatchStatusUpdates((previousUpdates) => [dispatchUpdate, ...previousUpdates]);
            }
          }
        } else if (DIGITAL_DISPATCH_LIVE_UPDATE_TYPE_LOCATION_STATUS === webPubSubUpdateType) {
          const isLocationUpdatePresent = dispatchLocationUpdates ? Boolean(dispatchLocationUpdates
            .find(({ id }) => id === dispatchUpdate.id)) : false;
          if (!isLocationUpdatePresent) {
            if (dispatchUpdate.digitalProviderNetwork === DIGITAL_DISPATCH_NETWORK_TOWBOOK) {
              const towBookDispatchUpdate = towBookDispatchLocationUpdateParser(dispatchUpdate);

              setDispatchLocationUpdates((previousUpdates) => [
                towBookDispatchUpdate, ...previousUpdates
              ]);
            } else {
              setDispatchLocationUpdates((previousUpdates) => [dispatchUpdate, ...previousUpdates]);
            }
          }
        } else if (DIGITAL_DISPATCH_LIVE_UPDATE_TYPE_POSTAPPROVAL_STATUS === webPubSubUpdateType) {
          const isPostApprovalUpdatePresent = Boolean(dispatchLocationUpdates
            .find(({ id }) => id === dispatchUpdate.id));
          if (!isPostApprovalUpdatePresent) {
            if (dispatchUpdate.digitalProviderNetwork === DIGITAL_DISPATCH_NETWORK_TOWBOOK) {
              const towBookDispatchUpdate = towBookDispatchPostApprovalUpdateParser(dispatchUpdate);

              setDispatchPostApprovalUpdates((previousUpdates) => [
                towBookDispatchUpdate, ...previousUpdates
              ]);
            } else {
              setDispatchPostApprovalUpdates((previousUpdates) => [
                dispatchUpdate, ...previousUpdates
              ]);
            }
          }
        }
      }
    } catch (error) {
      enqueueSnackbar(error?.message ?? 'Failed to parse incoming dispatch status update', {
        variant: 'error',
        preventDuplicate: true
      });
    } finally {
      setAreDispatchStatusUpdatesLoading(false);
    }
  }

  /**
   * Joins the specified Webpubsub group for listening
   * to the dispatch with the passed in dispatchId
   * @returns {Promise<void>}
   */
  const startListeningForDigitalDispatchUpdates = async () => {
    try {
      await digitalDispatchWebPubSubService.startClient();
      await digitalDispatchWebPubSubService.joinGroup();
      digitalDispatchWebPubSubService.listenForGroupMessages(addDispatchStatusUpdate);
    } catch (error) {
      enqueueSnackbar(error?.message ? `${error?.message}` : 'Failed to start webpubsub client', {
        variant: 'error',
        preventDuplicate: true
      });
    }
  };

  /**
   * Closes the current connection
   * @returns {Promise<void>}
   */
  const closeConnection = async () => {
    await digitalDispatchWebPubSubService.stopClient();
  };

  /**
   * Stop the WebPubSubClient instance from running
   * and re-fetch the client access URI and set up the
   * service again. Usually invoked once the client URI
   * access token TTL expires and to start the connection
   * @returns {Promise<void>}
   */
  const startConnection = async () => {
    if (isConnected) {
      await digitalDispatchWebPubSubService.stopClient();
    }
    await setUpWebPubSubClientInstance(dispatchId);
  };

  /**
   * Fetches the initial status updates/records stored in the logging table
   * associated with this particular dispatch specified by the dispatchId
   * @returns {Promise<void>}
   */
  async function getInitialDispatchStatusUpdates() {
    try {
      setAreDispatchStatusUpdatesLoading(true);
      const response = await digitalDispatchService.getDigitalDispatchDispatchStatus(dispatchId);

      const parsedStatusUpdates = response.data.map((statusUpdate) => {
        if (statusUpdate.digitalProviderNetwork === DIGITAL_DISPATCH_NETWORK_TOWBOOK) {
          const parsedUpdate = towBookDispatchStatusUpdateParser(statusUpdate);

          return parsedUpdate;
        }
        return statusUpdate;
      });

      setDispatchStatusUpdates(parsedStatusUpdates);
    } catch (error) {
      enqueueSnackbar(error?.message ?? 'Error fetching dispatch status updates', {
        preventDuplicate: true,
        variant: 'error'
      });
    } finally {
      setAreDispatchStatusUpdatesLoading(false);
    }
  }

  /**
   * Fetches the initial location updates/records stored in the logging table
   * associated with this particular dispatch specified by the dispatchId
   * @returns {Promise<void>}
   */
  async function getInitialDispatchLocationUpdates() {
    try {
      setAreDispatchLocationUpdatesLoading(true);
      const response = await digitalDispatchService.getDigitalDispatchLocationUpdates(dispatchId);

      const parsedLocationUpdates = response.data.map((locationUpdate) => {
        if (locationUpdate.digitalProviderNetwork === DIGITAL_DISPATCH_NETWORK_TOWBOOK) {
          const parsedUpdate = towBookDispatchLocationUpdateParser(locationUpdate);

          return parsedUpdate;
        }
        return locationUpdate;
      });

      setDispatchLocationUpdates(parsedLocationUpdates);
    } catch (error) {
      enqueueSnackbar(error?.message ?? 'Error fetching dispatch location updates', {
        preventDuplicate: true,
        variant: 'error'
      });
    } finally {
      setAreDispatchLocationUpdatesLoading(false);
    }
  }

  /**
   * Fetches the initial post approval updates/records stored in the logging table
   * associated with this particular dispatch specified by the dispatchId
   * @returns {Promise<void>}
   */
  async function getInitialDispatchPostApprovalUpdates() {
    try {
      setAreDispatchPostApprovalUpdatesLoading(true);
      const response = await digitalDispatchService
        .getDigitalDispatchPostApprovalEvents(dispatchId);

      const parsedPostApprovalUpdates = response.data.map((postApprovalUpdate) => {
        if (postApprovalUpdate.digitalProviderNetwork === DIGITAL_DISPATCH_NETWORK_TOWBOOK) {
          const parsedUpdate = towBookDispatchPostApprovalUpdateParser(postApprovalUpdate);

          return parsedUpdate;
        }
        return postApprovalUpdate;
      });
      setDispatchPostApprovalUpdates(parsedPostApprovalUpdates);
    } catch (error) {
      enqueueSnackbar(error?.message ?? 'Error fetching dispatch post approval updates', {
        preventDuplicate: true,
        variant: 'error'
      });
    } finally {
      setAreDispatchPostApprovalUpdatesLoading(false);
    }
  }

  useEffect(() => {
    if (dispatchId) {
      getInitialDispatchStatusUpdates();
      getInitialDispatchLocationUpdates();
      if (!omitPostApprovalEventsFetch) {
        getInitialDispatchPostApprovalUpdates();
      }
    }
  }, [dispatchId]);

  return {
    isConnected,
    setIsConnected,
    startListeningForDigitalDispatchUpdates,
    closeConnection,
    isFetchingClientAccessUri,
    startConnection,
    dispatchStatusUpdates,
    areDispatchStatusUpdatesLoading,
    dispatchLocationUpdates,
    areDispatchLocationUpdatesLoading,
    areDispatchPostApprovalUpdatesLoading,
    dispatchPostApprovalUpdates,
    setDispatchPostApprovalUpdates,
    webPubSubError,
    setWebPubSubError
  };
};

export default useDigitalDispatchLiveDispatchUpdates;
